import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import FontAwesome from 'react-fontawesome';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ImmutableInstitution as InstitutionPropType } from '../../../CustomPropTypes/InstitutionPropTypes';
import { ActionPromiseButton } from '../../components/Buttons/ActionButton/ActionPromiseButton';

import './styles.scss';
import {
  convertInstitutionToMenuOption,
  institutionFilterPredicate,
  institutionSortPredicate,
} from './helpers';

const URL_STATUS = {
  IDLE: '',
  VALIDATING: 'validating',
  VALID: 'valid',
  INVALID: 'invalid',
};

export class CreateCourseForm extends React.PureComponent {
  static propTypes = {
    institution: PropTypes.string, // default institution
    institutions: ImmutablePropTypes.listOf(InstitutionPropType).isRequired,
    underInstitutionPath: PropTypes.string, // current institution
    validateCourseNameChange: PropTypes.func.isRequired,
    validateCoursePathChange: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired, // this should return a promise
    submitButtonProps: PropTypes.shape({
      label: PropTypes.string,
      colour: PropTypes.string,
    }).isRequired,
    footnote: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    categories: ImmutablePropTypes.listOf(
      ImmutablePropTypes.mapContains({
        title: PropTypes.string.isRequired,
        code: PropTypes.string.isRequired,
      })
    ),
  };

  static defaultProps = {
    institution: null,
    footnote: null,
    categories: null,
    underInstitutionPath: null,
  };

  state = {
    courseName: '',
    coursePath: '',
    institution: null,
    institutionName: '',
    filteredInstitutions: [],
    urlStatus: URL_STATUS.IDLE,
    submitDisabled: true,
    courseNameRequestId: null,
    courseUrlRequestId: null,
    submitting: false,
    category: null,
  };

  componentDidMount() {
    this.setInstitutionFromProps(this.props);
  }

  componentWillReceiveProps(nextProps) {
    this.setInstitutionFromProps(nextProps);
  }

  // It is possible for a user to be an admin of an institution course
  // but not an admin/staff member of the institution itself
  // in which case, they cannot copy a course into that institution
  // so only set the institution if it has not yet been initialised in state
  // and exists in the user's allowed institutions list
  setInstitutionFromProps = (props) => {
    const { institution: selectedInstitution } = this.state;
    const { institution, institutions, underInstitutionPath } = props;

    if (
      selectedInstitution === null &&
      institution &&
      institutions.find((inst) => inst.get('path') === institution)
    ) {
      this.setState({ institution, filteredInstitutions: institutions });
    } else if (underInstitutionPath) {
      this.setState({ institution: underInstitutionPath });
    }
  };

  validateCourseNameChange = _.debounce(() => {
    const { validateCourseNameChange } = this.props;
    let { courseName } = this.state;

    courseName = courseName.trim();
    if (courseName) {
      const courseNameRequestId = _.uniqueId();
      this.setState({ courseNameRequestId });
      validateCourseNameChange(courseName, courseNameRequestId).then(
        ({ path, requestId: responseRequestId }) => {
          const { courseNameRequestId: currentRequestId } = this.state;
          if (responseRequestId === currentRequestId) {
            this.onCoursePathChange(path);
          }
        }
      );
    } else {
      this.onCoursePathChange('');
    }
  }, 300);

  onCourseNameChange = (event) => {
    const courseName = event.target.value;

    this.setState({ courseName, submitDisabled: true });
    this.validateCourseNameChange(courseName);
  };

  validateCoursePathChange = _.debounce(() => {
    const { validateCoursePathChange } = this.props;
    const { coursePath, institution } = this.state;
    if (coursePath) {
      if (coursePath.length < 3) {
        this.setState({
          urlStatus: URL_STATUS.INVALID,
        });
        return;
      }

      const courseUrlRequestId = _.uniqueId();
      this.setState({
        courseUrlRequestId,
        urlStatus: URL_STATUS.VALIDATING,
      });
      validateCoursePathChange(
        coursePath,
        institution,
        courseUrlRequestId
      ).then(
        ({ canUse, requestId: responseRequestId }) => {
          const { courseUrlRequestId: currentRequestId } = this.state;

          if (responseRequestId === currentRequestId) {
            this.setState({
              urlStatus: canUse ? URL_STATUS.VALID : URL_STATUS.INVALID,
            });
            this.validateForm();
          }
        },
        ({ requestId: responseRequestId }) => {
          const { courseUrlRequestId: currentRequestId } = this.state;

          if (responseRequestId === currentRequestId) {
            this.setState({ urlStatus: URL_STATUS.INVALID });
          }
        }
      );
    } else {
      this.setState({
        urlStatus: URL_STATUS.IDLE,
      });
    }
  }, 300);

  onCoursePathChange = (coursePath) => {
    this.setState({ coursePath, submitDisabled: true });
    this.validateCoursePathChange();
  };

  onCoursePathInputChange = (event) => {
    this.onCoursePathChange(event.target.value);
  };

  onInstitutionChange = (institution) => () => {
    this.setState({
      institution: institution ? institution.value : '',
      institutionName: institution ? institution.label : 'None',
      filteredInstitutions: [],
    });
    this.validateCoursePathChange();
  };

  getFilteredInstitutionsList(value) {
    const { institutions } = this.props;
    return institutions
      .map(convertInstitutionToMenuOption)
      .filter(institutionFilterPredicate(value))
      .sort(institutionSortPredicate)
      .splice(0, 0, { label: 'None', value: '' });
  }

  onInstitutionNameChange = (event) => {
    const { value } = event.target;
    const filteredInstitutions = this.getFilteredInstitutionsList(value);
    this.setState({ institutionName: value, filteredInstitutions });
    this.validateCoursePathChange();
  };

  onInstitutionFocus = () => {
    const filteredInstitutions = this.getFilteredInstitutionsList('');
    this.setState({ filteredInstitutions });
  };

  onCategoryChange = (event) => {
    this.setState({ category: event.target.value });
  };

  validateForm = () => {
    const { courseName, coursePath, urlStatus } = this.state;
    const valid = courseName && coursePath && urlStatus === URL_STATUS.VALID;
    this.setState({ submitDisabled: !valid });
  };

  onSubmitClick = () => {
    const { courseName, coursePath, institution, category } = this.state;
    const { onSubmit } = this.props;

    this.setState({ submitting: true });

    return onSubmit({
      courseName,
      coursePath,
      institution,
      category,
    }).then(
      () => {
        this.setState({ submitting: false });
      },
      () => {
        this.setState({ submitting: false });
      }
    );
  };

  renderTooltip = () => {
    const { urlStatus } = this.state;
    return (
      <Tooltip id="course-url-status">
        {urlStatus === URL_STATUS.VALID
          ? "That's great! You can use this URL."
          : 'This URL has been taken or cannot be used.'}
      </Tooltip>
    );
  };

  renderUrlStatus = () => {
    const { urlStatus } = this.state;
    if (urlStatus === URL_STATUS.VALID || urlStatus === URL_STATUS.INVALID) {
      return (
        <OverlayTrigger placement="top" overlay={this.renderTooltip()}>
          <FontAwesome
            name={urlStatus === URL_STATUS.VALID ? 'check' : 'times'}
          />
        </OverlayTrigger>
      );
    }
    return null;
  };

  render() {
    const {
      submitButtonProps,
      footnote,
      categories,
      institutions,
    } = this.props;
    const {
      courseName,
      coursePath,
      institution,
      institutionName,
      submitDisabled,
      submitting,
      category,
      filteredInstitutions,
    } = this.state;

    return (
      <create-course-form>
        <div className="controlGroup">
          <label htmlFor="courseName">Course name</label>
          <input
            autoSave="false"
            type="text"
            id="courseName"
            name="courseName"
            placeholder="Course name"
            value={courseName}
            onChange={this.onCourseNameChange}
            disabled={submitting}
          />
        </div>
        <div className="controlGroup">
          <label htmlFor="coursePath">Course URL</label>
          <div className="course-url">
            <div className="inputBeforePath" id="inputBeforePath">
              openlearning.com/{institution ? `${institution}/` : null}
              courses/
            </div>
            <input
              autoSave="false"
              id="coursePath"
              name="coursePath"
              type="text"
              placeholder="coursename"
              value={coursePath}
              onChange={this.onCoursePathInputChange}
              disabled={submitting}
            />
            <div className="urlStatus">{this.renderUrlStatus()}</div>
          </div>
        </div>
        {categories && categories.size > 0 && (
          <div className="controlGroup">
            <label htmlFor="category">Course Category</label>
            <select
              autoSave="false"
              id="category"
              name="category"
              value={category || ''}
              onChange={this.onCategoryChange}
              disabled={submitting}
            >
              <option key="none" value="">
                None
              </option>
              {categories.map((cate) => (
                <option key={cate.get('code')} value={cate.get('code')}>
                  {cate.get('title')}
                </option>
              ))}
            </select>
          </div>
        )}
        {institutions.size > 0 && (
          <div className="controlGroup create-course-form--institution">
            <label htmlFor="institution">Institutions</label>
            <div>
              <input
                autoSave="false"
                type="text"
                id="institution"
                name="institution"
                placeholder="Institution"
                value={institutionName}
                onFocus={this.onInstitutionFocus}
                onChange={this.onInstitutionNameChange}
                disabled={submitting}
              />
              {/* double up on check here because when list length is 0 we have type Array
                  but when length is > 0 we have type ImmutablePropTypes.listOf(InstitutionPropType)
                  TODO: create a new component for this (autocomplete input with dropdown options) */}
              {filteredInstitutions.length > 0 ||
                (filteredInstitutions.size > 0 && (
                  <div className="create-course-form--filtered-institutions">
                    {filteredInstitutions.map((inst) => (
                      <div
                        className="create-course-form--filtered-institution-item"
                        key={inst.value}
                        role="button"
                        tabIndex={0}
                        onKeyPress={this.onInstitutionChange(inst)}
                        onMouseDown={this.onInstitutionChange(inst)}
                      >
                        {inst.label}
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          </div>
        )}
        {footnote}
        <ActionPromiseButton
          {...submitButtonProps}
          disabled={submitDisabled}
          onClick={this.onSubmitClick}
        />
      </create-course-form>
    );
  }
}
